import { isServer } from './utils';

export const PREVIEW_MODE_PARAM = '__preview';
export const PREVIEW_MODE_DATE_PARAM = '__preview_date';
export const PREVIEW_REFRESH_PATHNAME = '/__pwa-kit/refresh';

/**
 * This list contains domains that can host code in iframe
 */
export const IFRAME_HOST_ALLOW_LIST = new Set([
    'https://runtime.commercecloud.com',
    'https://runtime-admin-staging.mobify-storefront.com',
    'https://runtime-admin-preview.mobify-storefront.com',
]);

export const detectInIframe = (): boolean =>
    typeof window !== 'undefined' && window.top !== window.self;

export const getParentOrigin = (): string => {
    if (detectInIframe()) {
        // location.ancestorOrigins[0] will provide the parent host url, but it only works for Chrome and Safari.
        // Firefox does not have this field. document.referrer is common use for parent url, but it could be less reliable.
        // It is best to use it a fallback option for Firefox
        if (window.location.ancestorOrigins) return window.location.ancestorOrigins[0];
        if (document.referrer) return new URL(document.referrer).origin;
    }
    return '';
};

export const detectStorefrontPreview = (): boolean =>
    detectInIframe() && IFRAME_HOST_ALLOW_LIST.has(getParentOrigin());

export const isNotAccessibleTopOrigin = (): boolean => {
    const { top } = window;
    // if not iframe return false, since it is not cross-origin
    if (window.self === window.top) {
        return false;
    }
    try {
        const topLocation = top?.location.origin ?? null;
        if (!topLocation) {
            return true; // not accessible top origin, since it is null
        }
        // if somehow the top origin is accessible, check if it is cross-origin
        return topLocation !== new URL(window.location.href).origin;
    } catch (error) {
        return true; // not accessible top origin, since it failed to get the origin
    }
};

export function isValidDateString(dateString: string): boolean {
    if (!dateString) {
        return false;
    }
    try {
        const date = new Date(dateString);
        return !Number.isNaN(date.valueOf());
    } catch (error) {
        return false;
    }
}

export function cleanEffectiveDateTime(dateTimeString: string | undefined | null): string {
    if (!dateTimeString) {
        return '';
    }
    if (isValidDateString(dateTimeString)) {
        return new Date(dateTimeString).toISOString();
    }
    return '';
}

/**
 * Replaces the __preview_date param in the current URL with the given date string
 * and reloads the page.
 * If no date is passed, the __preview_date param will be removed.
 * Also updates the __preview param to the current timestamp for cache-busting.
 *
 * @param {string} date - The date string to set as __preview_date. If falsy, removes the param.
 */
export function setPreviewDateInUrl(date?: string | null): void {
    if (isServer) {
        return;
    }
    const url = new URL(window.location.href);
    const urlDate = cleanEffectiveDateTime(url.searchParams.get(PREVIEW_MODE_DATE_PARAM));
    const cleanedDate = cleanEffectiveDateTime(date);
    if (cleanedDate !== urlDate) {
        if (cleanedDate) {
            url.searchParams.set(PREVIEW_MODE_DATE_PARAM, cleanedDate);
            url.searchParams.set(PREVIEW_MODE_PARAM, Date.now().toString());
        } else {
            url.searchParams.delete(PREVIEW_MODE_DATE_PARAM);
            url.searchParams.delete(PREVIEW_MODE_PARAM);
        }
        window.location.replace(url.toString());
    }
}

/**
 * Converts a Date object to a local date time string in the format of YYYY-MM-DDTHH:MM:SS
 * @param date - The Date object to convert
 * @returns The local date time string
 */
export function toLocalDateTimeString(date: Date): string {
    const padZeros = (value: number, zeros: number): string => String(value).padStart(zeros, '0');
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const hour = date.getHours();
    const minute = date.getMinutes();
    const second = date.getSeconds();
    return `${padZeros(year, 4)}-${padZeros(month, 2)}-${padZeros(day, 2)}T${padZeros(hour, 2)}:${padZeros(minute, 2)}:${padZeros(second, 2)}`;
}

export function normalizeEffectiveDateTime(dateTime: string): string {
    if (!isValidDateString(dateTime)) {
        return '';
    }
    return new Date(dateTime).toISOString().replace('.000Z', 'Z');
}
